import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  units: [],
  unitLevels: [],
  unitStatuses: [],
  verifyModes: [],
  availableLicenses: [],
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'name',
  sortDirection: 'asc',
  filterName: '',
  filterLevel: '',
  filterStatus: '',
  filterLicense: '',
  loading: false,
  error: null,
  successMessage: null,
  selectedUnit: null,
};

const licenseUnitsSlice = createSlice({
  name: 'licenseUnits',
  initialState,
  reducers: {
    // Action to trigger fetch init data (saga will handle this)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.unitLevels = action.payload.unitLevels || [];
      state.unitStatuses = action.payload.unitStatuses || [];
      state.verifyModes = action.payload.verifyModes || [];
      state.availableLicenses = action.payload.availableLicenses || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch units (saga will handle this)
    fetchUnits: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUnitsSuccess: (state, action) => {
      state.loading = false;
      state.units = action.payload.data || [];
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchUnitsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add unit
    addUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    addUnitSuccess: (state, action) => {
      state.loading = false;
      state.units.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('licenseUnits.success.created');
    },
    addUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single unit
    fetchUnitById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUnitByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedUnit = action.payload;
      state.error = null;
    },
    fetchUnitByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update unit
    updateUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateUnitSuccess: (state, action) => {
      state.loading = false;
      const index = state.units.findIndex((unit) => unit.id === action.payload.id);
      if (index !== -1) {
        state.units[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('licenseUnits.success.updated');
    },
    updateUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update unit status
    updateUnitStatus: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateUnitStatusSuccess: (state, action) => {
      state.loading = false;
      const index = state.units.findIndex((unit) => unit.id === action.payload.id);
      if (index !== -1) {
        state.units[index] = { ...state.units[index], ...action.payload };
      }
      state.error = null;
      state.successMessage = i18n.t('licenseUnits.success.statusUpdated');
    },
    updateUnitStatusFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete unit
    deleteUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteUnitSuccess: (state, action) => {
      state.loading = false;
      state.units = state.units.filter((unit) => unit.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('licenseUnits.success.deleted');
    },
    deleteUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete units
    bulkDeleteUnits: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteUnitsSuccess: (state, action) => {
      state.loading = false;
      state.units = state.units.filter((unit) => !action.payload.includes(unit.id));
      state.error = null;
      state.successMessage = i18n.t('licenseUnits.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteUnitsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected unit
    setSelectedUnit: (state, action) => {
      state.selectedUnit = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Clear selected unit
    clearSelectedUnit: (state) => {
      state.selectedUnit = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1;
    },
    setFilterLevel: (state, action) => {
      state.filterLevel = action.payload;
      state.page = 1;
    },
    setFilterStatus: (state, action) => {
      state.filterStatus = action.payload;
      state.page = 1;
    },
    setFilterLicense: (state, action) => {
      state.filterLicense = action.payload;
      state.page = 1;
    },
  },
});

// Export actions
export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  fetchUnitById,
  fetchUnitByIdSuccess,
  fetchUnitByIdFailure,
  addUnit,
  addUnitSuccess,
  addUnitFailure,
  updateUnit,
  updateUnitSuccess,
  updateUnitFailure,
  updateUnitStatus,
  updateUnitStatusSuccess,
  updateUnitStatusFailure,
  deleteUnit,
  deleteUnitSuccess,
  deleteUnitFailure,
  bulkDeleteUnits,
  bulkDeleteUnitsSuccess,
  bulkDeleteUnitsFailure,
  setSelectedUnit,
  clearError,
  clearSuccessMessage,
  clearSelectedUnit,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setFilterLevel,
  setFilterStatus,
  setFilterLicense,
} = licenseUnitsSlice.actions;

// Selectors
export const selectLicenseUnits = (state) => state.licenseUnits.units;
export const selectUnitLevels = (state) => state.licenseUnits.unitLevels;
export const selectUnitStatuses = (state) => state.licenseUnits.unitStatuses;
export const selectVerifyModes = (state) => state.licenseUnits.verifyModes;
export const selectAvailableLicenses = (state) => state.licenseUnits.availableLicenses;
export const selectLicenseUnitsLoading = (state) => state.licenseUnits.loading;
export const selectLicenseUnitsError = (state) => state.licenseUnits.error;
export const selectLicenseUnitsSuccessMessage = (state) => state.licenseUnits.successMessage;
export const selectSelectedUnit = (state) => state.licenseUnits.selectedUnit;
export const selectLicenseUnitsTotal = (state) => state.licenseUnits.total;
export const selectLicenseUnitsPage = (state) => state.licenseUnits.page;
export const selectLicenseUnitsPageSize = (state) => state.licenseUnits.pageSize;
export const selectLicenseUnitsSortColumn = (state) => state.licenseUnits.sortColumn;
export const selectLicenseUnitsSortDirection = (state) => state.licenseUnits.sortDirection;
export const selectLicenseUnitsFilterName = (state) => state.licenseUnits.filterName;
export const selectLicenseUnitsFilterLevel = (state) => state.licenseUnits.filterLevel;
export const selectLicenseUnitsFilterStatus = (state) => state.licenseUnits.filterStatus;
export const selectLicenseUnitsFilterLicense = (state) => state.licenseUnits.filterLicense;

// Export reducer
export default licenseUnitsSlice.reducer;
